import { combineLatest, EMPTY, NEVER, Observable, of as observableOf, of, Subject } from "rxjs";
import { Big } from "big.js";

import { catchError, filter, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { AfterViewInit, ChangeDetectorRef, Component, inject, Injector, Input, OnDestroy, OnInit } from "@angular/core";
import { SendingComponent } from "../sending-modal";
import {
	Contact,
	ContactField,
	Conversion,
	Cryptocoin,
	CryptoWallet,
	Fiat,
	FiatWallet,
	Offer,
	Price,
	Validateable
} from "@models";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { ModalHeader } from "@models/modal_step";
import { Helper } from "@helpers/functions";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { config } from "@config";
import { equal, stripFunnyInput, truncate } from "@helpers/utils";
import { CryptoTransaction, FiatTransaction, GenericTransaction, SendTransaction } from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { ContactService } from "@main-services/contact.service";
import { CryptocoinService, FiatService } from "@main-services/currency";
import { UserService } from "@main-services/user.service";
import { SocketService } from "@main-services/socket.service";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { InfoService } from "@core-services/info.service";
import { TradeBuyComponent } from "@modules/trade/modals/trade-buy-modal/trade-buy-modal.component";
import { SendSuccessComponent } from "../../success-screens/send-success/send-success.component";
import { SendWaitingComponent } from "../../success-screens/send-waiting/send-waiting.component";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { BpSelectOption } from "@modules/core/components/bp-select/bp-select.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { TranslationPipe } from "@modules/core/pipes/translation-pipe/translation.pipe";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { WalletBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance-status.model";
import {
	ButtonStatus,
	SendEntryPointService
} from "@modules/+new/payments/features/send/domain/send-entry-point.service";
import { FiatPrecisionSymbolPipe } from "@modules/core/pipes";
import { Fee } from "@modules/main/components/step-summary/sending-modal/entities/fee.model";
import { RecipientInfoModel } from "@modules/main/components/step-summary/sending-modal/entities/recipient-info.model";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";

@UntilDestroy()
@Component({
	templateUrl: "./send-modal.component.html",
	styleUrls: ["./send-modal.component.scss"],
	standalone: false
})
export class SendModalComponent
	extends SendingComponent<Cryptocoin, CryptoWallet>
	implements AfterViewInit, OnDestroy, OnInit
{
	private cashPlusPortfolioFacade = inject(CashplusPortfolioFacade);
	private cashPlusFacade = inject(CashPlusFacade);
	public hasSuffFunds = false;

	@Input()
	public perfectInputMatch: boolean = false;
	@Input() isBackFromMultichain: boolean = false;
	public selectedContact: Contact = null;
	public getDtTypes: BpSelectOption[] = SendTransaction.Types;
	public preselected: CryptoWallet = null;
	public paymentsFlow: boolean = false;
	public paymentsFlowSuccess: boolean = false;
	public fiatTx: FiatTransaction = null;
	public forceHideFiatSend: boolean = false; // When Send is launched from Contacts
	public show: boolean = true;
	public validationRunning: boolean = false;
	public showExternalWarning: boolean = false; // Warning message step
	public warningSlider: SendSwitch = new SendSwitch();
	public walletBalance: WalletBalanceStatus = null;
	protected label: string = "send";
	protected readonly flowName: string = "Send Assets";
	protected readonly pathPrepend: string = "/send/assets";
	private confirm: boolean = false;
	private fee: Fee = new Fee();
	private savedWallet: CryptoWallet = null;
	private translationPipe: TranslationPipe = null;
	private hasSendFiatWallets: boolean = false;
	private priorRecipient: string[] = [];
	private maxApiAmount: Conversion = new Conversion();
	private pageChangeSubject: Subject<void> = new Subject();
	// ---------------------- IN CASE OF SEND-TYPE BOTH, INTERNAL AND EXTERNAL NEEDS TO BE TEMP-SAVED
	private limitsCollection: LimitsCollection = new LimitsCollection();
	public amountInvalidMax: boolean = false;
	public cryptocoinItems: Cryptocoin[] = [];
	public isMultichain$: Observable<boolean> = EMPTY;
	private canDismiss: boolean = false;
	private launchDarklyService = inject(LaunchDarklyService);
	private trackSendRudderstackService = inject(SendTrackingFacade);

	//Cashplus
	private fiatWalletWithBalance$ = this.fiatWalletService.getList().pipe(
		// first filter the wallet
		map((fiatWallets) =>
			fiatWallets.filter((w) => w.getBalance() > 0 && this.isWalletSendEnabled(w.getCurrencyId()))
		)
	);

	public getSendFiatWallets$ = combineLatest([
		this.cashPlusFacade.isCashPlusEnabled$(),
		this.fiatWalletWithBalance$
	]).pipe(
		switchMap(([enabled, fiatWallets]) => {
			if (enabled) {
				return this.combineCashPlusBalanceHelper$(fiatWallets);
			}

			return of(fiatWallets);
		})
	);

	public constructor(
		private apicallService: ApicallService,
		private contactService: ContactService,
		private infoService: InfoService,
		private injector: Injector,
		protected fiatService: FiatService,
		protected fiatWalletService: FiatWalletService,
		protected masterdataService: MasterdataService,
		private userService: UserService,
		private socketService: SocketService,
		protected accountService: AccountService,
		private cryptoWalletService: CryptoWalletService,
		private cryptoCoinService: CryptocoinService,
		private ref: ChangeDetectorRef,
		protected confirmModalService: ConfirmModalService,
		protected walletBalancesFacade: WalletsBalancesFacadeService,
		protected sendEntryPoint: SendEntryPointService
	) {
		super(masterdataService, accountService, fiatService, confirmModalService);
		this.translationPipe = new TranslationPipe(this.translationService);
	}

	ngOnInit(): void {
		this.cryptocoinItems = this.items;
		this.isMultichain$ = this.launchDarklyService.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN);
		this.isMultichain$.pipe(take(1)).subscribe((isMultichain) => {
			if (isMultichain) {
				this.trackSendRudderstackService.trackSendFlowStarted();
			}
		});
		this.hasSuffFunds = this.hasSufficientFunds();
		super.ngOnInit();
	}

	// ----------------------

	private _contactField: ContactField = null;

	public get contactField(): ContactField {
		return this._contactField;
	}

	public set contactField(c: ContactField) {
		this._contactField = c;
	}

	public get isFirstStep(): boolean {
		return (this.wallet == null && this.isEdit(0)) || this.isEdit(1);
	}

	public get isSecondStep(): boolean {
		return (this.wallet != null && !this.isContactSelected() && this.isEdit(0)) || this.isEdit(2);
	}

	public get isThirdStep(): boolean {
		return (
			((this.wallet != null && this.isContactSelected() && this.isEdit(0)) || this.isEdit(3)) &&
			!this.showExternalWarning &&
			!!this.price
		);
	}

	public get isFourthStep(): boolean {
		return (
			((this.wallet != null && this.isContactSelected() && this.isEdit(0)) || this.isEdit(3)) &&
			this.showExternalWarning
		);
	}

	public get item(): Cryptocoin {
		return this.cryptoCoinService.getItemById(this.wallet.getCurrencyId());
	}

	public get icon(): string {
		return this.userWants === "cryptocoin" ? this.item.getLogo() : this.fiat.getLogo();
	}

	public get wallets(): CryptoWallet[] {
		return this.filteredCryptoWallets();
	}

	public get wallet(): CryptoWallet {
		return this.cryptoWalletService.getItemById(this.data.transaction.getWalletId());
	}

	public set wallet(w: CryptoWallet) {
		this.data.transaction.setWallet(w);
		this.walletBalancesFacade
			.getCryptoBalancesStatus$()
			.pipe(take(1))
			.toPromise()
			.then((walletBalanceStatus) => {
				this.walletBalance = {
					balance: walletBalanceStatus.balances.find(
						(walletBalance) => w?.getId() === walletBalance.walletGuid
					),
					hasUnconfirmedBalance: walletBalanceStatus.hasUnconfirmedBalance
				};
			});
	}

	public get items(): Cryptocoin[] {
		return this.cryptoCoinService.list.filter(
			(c) =>
				this.wallets.some((w) => w.getCurrencyId() === c.getId() && w.getBalance() > 0) && c.isWithdrawActive()
		);
	}

	public get contacts(): Contact[] {
		return [...this.contactService.contacts].filter((c) => !c.isDeleted()).filter((c) => c.getFields().length > 0);
	}

	public get multiplyTradableAmountByFiatPrice(): number {
		const price = super.multiplyTradableAmountByFiatPrice;
		const fee = this.feeAmountFiat;
		if (this.data.transaction._try_internal) {
			return price;
		}
		return price + fee;
	}

	public get feeAmountFiat(): number {
		return this.toFiatAmount(this.getFeeAmount());
	}

	public get amount(): string {
		return this.userWants === "cryptocoin"
			? this.data.transaction.getAmountCoin()
			: this.data.transaction.getAmountFiat();
	}

	public set amount(n: string) {
		if (typeof n !== "string") {
			console.error("set amount not string: " + JSON.stringify(n) + " " + ("" + n));
			return;
		}
		n = stripFunnyInput(n);
		if (this.userWants === "cryptocoin") {
			this.data.transaction.setAmountCoin(n);
			this.data.transaction.setAmountFiat("" + this.toFiatAmount(+n));
		} else {
			this.data.transaction.setAmountFiat(n);
			this.data.transaction.setAmountCoin("" + this.toItemAmount(+n));
		}
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (
			(this.isFirstStep && this.tradableId) ||
			(this.isSecondStep && this.isEdit(0) && !this.isDirectOffer()) ||
			(this.isThirdStep && this.isEdit(0))
		) {
			header.left = "back";
		} else if ((this.isFirstStep && !this.isEdit(0)) || (this.isSecondStep && this.isEdit(2))) {
			header.left = "abort";
		}
		if (
			(this.isFirstStep && (this.isEdit(1) || (this.isEdit(0) && this.hasWallets() && !!this.preselected))) ||
			(this.isSecondStep && this.isEdit(2))
		) {
			header.right = "ok";
		} else if ((this.isFirstStep && this.isEdit(0)) || (this.isSecondStep && !this.isEdit(2)) || this.isThirdStep) {
			header.right = "abort";
		} else if (this.isFourthStep) {
			header.left = "back";
			header.right = "abort";
		}

		return header;
	}

	// Override
	public get stepNumber(): number {
		return this.isFirstStep ? 1 : this.isSecondStep ? 2 : this.isThirdStep ? 3 : this.isFourthStep ? 4 : 0;
	}

	public ngAfterViewInit(): void {
		this.getDtTypes.forEach((item) => (item.name = this.translationService.translate(item.name)));
		const walletService = this.cryptoWalletService;

		if (walletService && !walletService.wallet && this.data.transaction.getWalletId()) {
			walletService.wallet = walletService.getItemById(this.data.transaction.getWalletId());
		}

		if (!!walletService && walletService.wallet != null) {
			const preselected = walletService.wallet;
			const coin: Cryptocoin = this.cryptoCoinService.getItemById(preselected.getCurrencyId());
			if (coin.isWithdrawActive() && !coin.isMaintenance()) {
				this.data.transaction["preselected"] = walletService.wallet;
			} else {
				walletService.wallet = null;
			}
			if (this.data.transaction.getWalletId() != null && this.isDirectOffer()) {
				this.wallet = walletService.getItemById(this.data.transaction.getWalletId());
			}
		}

		super.ngAfterViewInit();

		this.initTracking();

		// if we have a preselected wallet, we can skip the first step
		this.hasSendFiatWallets = this.fiatWalletService.getList().getValue().length > 0;
	}

	// helper function to combine cashplus balance if exists
	private combineCashPlusBalanceHelper$(fiatWallets: FiatWallet[]): Observable<FiatWallet[]> {
		if (fiatWallets?.length > 0) {
			const pids = fiatWallets.map((w) => {
				const pid = this.fiatService.getItemById(w.getFiatId()).getPid();
				return pid;
			});

			return combineLatest(pids.map((f) => this.cashPlusPortfolioFacade.getPortfolioPerFiatState$(f))).pipe(
				map((portfolios) =>
					fiatWallets.map((fw) => {
						const fnd = portfolios.find(
							(v) => v?.asset_pid === this.fiatService.getItemById(fw.getFiatId()).getPid()
						);

						if (fnd) {
							fw.setCashPlusBalance(+fnd.parked_asset_balance);
						}

						return fw;
					})
				)
			);
		}
		return of([]);
	}

	public getFiatById(fiatId: string): Fiat {
		return this.fiatService.getItemById(fiatId);
	}

	public resetPaymentsFlow(): void {
		this.paymentsFlow = false;
		this.fiatTx = null;
	}

	public isTradableListSelection(): boolean {
		return this.isFirstStep && !this.tradableId;
	}

	public async startPaymentsFlow(f: FiatWallet): Promise<void> {
		const fiat: Fiat = this.fiatService.getItemById(f.getFiatId());

		await this.sendEntryPoint
			.getStatusForFiatWallet$(f.getId())
			.pipe(take(1))
			.toPromise()
			.then((buttonStatus: ButtonStatus) => {
				if (buttonStatus.isDisabled) {
					const fiatMinWithdrawalAmount: string = fiat.getMinWalletWithdraw();
					const fiatPrecisionSymbolPipe = new FiatPrecisionSymbolPipe(
						this.fiatService,
						this.translationService
					);
					const translateProp: string = fiatPrecisionSymbolPipe.transform(fiatMinWithdrawalAmount, {
						id: fiat?.getId(),
						currencies: [fiat]
					});

					this.infoService.setError(this.translate(buttonStatus.message, [translateProp]));
					return;
				}

				this.fiatTx = new FiatTransaction();
				this.fiatTx["preselected"] = f;
				this.fiatTx.setType("withdrawal");
				this.fiatTx.setFiatWalletId(f.getId());
				this.fiatTx.setDirectFiatTransaction(true);
				this.paymentsFlow = true;
				this.ref.markForCheck();
			});
	}

	public hasWallets(): boolean {
		return super.hasWallets() || this.hasSendFiatWallets;
	}

	public getSendingType(): string {
		if (this.hasEnoughFundsForExternal()) {
			return this.fee.getAllowSending();
		}
		return "internal";
	}

	public showLowFundsWarning(): boolean {
		return !this.hasEnoughFundsForExternal() && this.fee.getAllowSending() === "both";
	}

	public hasEnoughFundsForExternal(): boolean {
		return (
			+this.price.getMaxAmountExternal().tradableAmount() >= +this.price.getMinAmountExternal().tradableAmount()
		);
	}

	public isBitpandaAddress(): boolean {
		return this.getSendingType() === "both";
	}

	public getInputFormat(): string {
		if (this.checkZeroPrecision()) {
			return "zeroPrecision";
		}
		return this.userWants;
	}

	public checkZeroPrecision(): boolean {
		return this.item.getPrecisionForTx() === 0 && !this.data.transaction.isInternalSendType();
	}

	public getFeeAmount(): number {
		return this.data.transaction.getTryInternal() ? 0 : this.fee.fee;
	}

	public getSendModalToolTipLink(s: string): string {
		if (s === "switchSend") {
			return this.translationService.getHdLink() + config.externalPaths["send"]["switchSend"];
		}

		if (s === "fee") {
			return this.translationService.getHdLink() + config.externalPaths["send"]["tooltip_fee"];
		}
	}

	public checkTryInternalTypeEdgecases(): void {
		if (this.onlyInternalSending()) {
			this.data.transaction.setTryInternal(true);
		} else if (this.onlyExternalSending()) {
			this.data.transaction.setTryInternal(false);
		} else {
			this.data.transaction.setTryInternal(
				this.isBitpandaAddress() ? true : this.data.transaction.isInternalSendType()
			);
		}
	}

	public onlyExternalSending(): boolean {
		return this.isBitpandaAddress() && this.item.isDepositActive() && !this.item.isTransferActive();
	}

	public onlyInternalSending(): boolean {
		return this.isBitpandaAddress() && !this.item.isDepositActive() && this.item.isTransferActive();
	}

	public isDirectOffer(): boolean {
		return this.data.transaction.getDirectTradableWalletOffer();
	}

	public getContactByDestinationTag(field: ContactField): Contact {
		return this.contactService.findContactByDestinationTag(field);
	}

	public isStellar(): boolean {
		if (!this.wallet) {
			return false;
		}
		return this.item.isStellar();
	}

	public recipientAdded(recInfo: RecipientInfoModel): void {
		this.data.transaction.setRecipient(recInfo.recipientAddress);
		this.data.transaction.setDestinationTag(recInfo.destinationTag);
		this.setPriceByType(recInfo.price, recInfo.sendType);
		this.initAmountScreen(recInfo.price, recInfo.fee, "");
		this.contactField = recInfo.contactField;
		if (!this.isSameRecipient()) {
			this.data.transaction.setIsExternal(!this.data.transaction.isInternalSendType());
			this.clearAmounts();
			this.edit = 0;
		}
		this.summary = !this.isEdit(0);
		if (this.summary) {
			this.data.transaction.setContactName(this.getContactByDestinationTag(this.contactField).getName());
		}
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
		this.confirm = true;
		this.trackSelect("recipient");
		this.ref.markForCheck();
	}

	public isWalletStackSelection(): boolean {
		return this.isFirstStep && !!this.tradableId;
	}

	public selectWallet(wallet: CryptoWallet): void {
		this.trackSelect("wallet");

		this.edit = 0;

		if (this.savedWallet && wallet.getCurrencyId() !== this.savedWallet.getCurrencyId()) {
			this.clearField();
		}

		if (this.wallet == null) {
			this.wallet = wallet;
			this.preselected = null;
			this.stellarTag();
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}
		if (this.wallet.getId() === wallet.getId()) {
			this.summary = true;
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}

		this.clearField();
		// 'resets' this view
		delete this.data.transaction["amount"];
		this.confirm = false;
		this.wallet = wallet;
		this.stellarTag();
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public setEdit(n: number, b: boolean): void {
		this.priorRecipient = [this.data.transaction.getRecipient(), this.data.transaction.getDestinationTag()];
		this.setPerfectInputMatch();
		super.setEdit(n, b);
		this.pageChangeSubject.next();
	}

	public stepBack(): void {
		if (this.confirm) {
			this.confirm = false;
			this.data.transaction.setRecipient("");
			this.data.transaction.setDestinationTag("");

			this.contactField = null;
			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}

		if (!this.isContactSelected()) {
			this.savedWallet = this.wallet;
			this.wallet = null;
			this.tradableId = null;
		} else {
			this.contactField = null;
		}
		this.confirm = false;
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public isVerificationMandatory(): boolean {
		return this.userService.isVerificationMandatory;
	}

	public isSameRecipient(): boolean {
		return (
			equal(this.data.transaction.getRecipient(), this.priorRecipient[0]) &&
			equal(this.data.transaction.getDestinationTag(), this.priorRecipient[1])
		);
	}

	public clearRecentError(): void {
		if (this.selectedContact) {
			this.selectedContact = null;
		}
		if (this.data.transaction.hasError("recipient")) {
			this.data.transaction.clearError();
		}

		this.setPerfectInputMatch();
	}

	public clearField(): void {
		this.data.transaction.clearError();
		this.data.transaction.recipient = "";
		this.contactField = null;
		this.confirm = false;
	}

	public isContactSelected(): boolean {
		return this.contactField != null || (this.data.transaction.getRecipient().trim().length > 0 && this.confirm);
	}

	public getSearchedContactFields(): ContactField[] {
		if (this.selectedContact) {
			return this.selectedContact
				.getFields(false, true)
				.filter((f) => f.getType() === "email" || f.getType().includes(this.item.getSymbol(true)));
		}

		const searchString = this.data.transaction.recipient.toLocaleLowerCase();
		return (
			searchString.trim() === ""
				? []
				: this.contacts
						.filter((contact) => contact.getName().toLocaleLowerCase().indexOf(searchString) > -1) // Find all matching contacts
						.reduce((contacts, contact) => contacts.concat(contact.getFields()), []) // Get their fields
						.concat(
							this.contacts // Find all matching fields
								.reduce((contacts, contact) => contacts.concat(contact.getFields()), [])
								.filter(
									(field: ContactField) =>
										field.getName().toLocaleLowerCase().indexOf(searchString) > -1 ||
										field.getValue().toLocaleLowerCase().indexOf(searchString) > -1
								)
						)
		)
			.sort((cf: ContactField, cf2: ContactField) => cf2.lastUsed() - cf.lastUsed()) // Sort by last used desc
			.filter((el, i, a) => i === a.indexOf(el)) // Filter Duplicates
			.filter(
				(field) => field.getSymbol() === "EMAIL" || field.getSymbol() === this.item.getSymbol().toUpperCase()
			); // Only show possible addresses for the wallet the user chose
	}

	public min(): string {
		const conv = this.data.transaction.isInternalSendType()
			? this.price.getMinAmountInternal()
			: this.price.getMinAmountExternal();
		return conv.tradableAmount();
	}

	public max(): string {
		const conv = this.data.transaction.isInternalSendType()
			? this.price.getMaxAmountInternal()
			: this.price.getMaxAmountExternal();
		return conv.tradableAmount();
	}

	public getStepsFromOfferFiat(): number {
		return this.fiatService.stepSizeByItem(this.fiat);
	}

	public maxAmount(): Conversion {
		return this.getMaxApiAmount();
	}

	public amountItemTotal(): string {
		const value = Math.min(+this.data.transaction.getAmountCoin(), +this.getMaxApiAmount().tradableAmount());
		if (value < +this.min()) {
			return "0";
		}

		if (this.data.transaction._try_internal) {
			return isNaN(value) ? "0" : "" + value;
		}
		const decimalValue = new Big(value);
		const decimalFee = new Big(this.fee.fee);
		const result = decimalValue.plus(decimalFee);
		return isNaN(value + this.fee.fee) ? "0" : "" + result.toString();
	}

	public setSendAmount(n: Conversion): void {
		super.setAmount(n);
		if (this.checkZeroPrecision()) {
			this.amount = "" + truncate(+this.amount, 0);
		}

		this.rudderstackTrack("Specific Amount Button Used", {
			button_amount: n.fiatAmount(),
			currency: this.fiat.getSymbol(true),
			flow: this.flowName
		});
		this.ref.markForCheck();
	}

	public setSendMaxAmount(): void {
		super.setMaxAmount();
		if (this.checkZeroPrecision()) {
			this.amount = "" + truncate(+this.amount, 0);
		}

		this.rudderstackTrack("Max Amount Button Used", {
			button_amount: "" + this.maxAmount().tradableAmount(),
			currency: this.item.getSymbol(true),
			flow: this.flowName
		});
		this.ref.markForCheck();
	}

	public gotoSummary(): void {
		if (!this.hasSufficientFunds()) {
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
			return;
		}

		this.track("summary");
		this.data.transaction.recipient = this.contactField.getValue();
		this.data.transaction.setDestinationTag(this.contactField.getDestinationTag());
		this.data.transaction.setDtType(this.contactField.dt_type);
		this.data.transaction.setIsExternal(!this.data.transaction.isInternalSendType());
		this.data.transaction.setContactName(this.getContactByDestinationTag(this.contactField).getName());

		if (!this.data.transaction.isInternalSendType() && this.item.getPrecisionForTx() !== 0) {
			this.data.transaction.setAmountCoin(
				"" + truncate(this.data.transaction.getAmountCoin(), this.item.getPrecisionForTx())
			);
		}

		if (!this.hasSufficientFunds()) {
			return;
		}

		if (+this.data.transaction.getAmountCoin() > +this.getMaxApiAmount().tradableAmount()) {
			this.infoService.setError(this.translationService.translate("send_amount_larger_than_wallet_balance"));
			this.userWants = "cryptocoin";
			this.amountInvalidMax = true;
			this.amount = this.getMaxApiAmount().tradableAmount();

			this.doTrapFocusTime();
			this.pageChangeSubject.next();
			return;
		}
		this.amountInvalidMax = false;

		if (+this.data.transaction.getAmountCoin() < +this.min()) {
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
			this.userWants = "cryptocoin";
			this.amount = this.min();
			this.doTrapFocusTime();
		} else {
			// Show the warning message with slider for withdrawal
			this.showExternalWarning = true;
		}
		this.pageChangeSubject.next();
		this.ref.markForCheck();
	}

	public showSuccessScreen(transaction: CryptoTransaction): void {
		this.cryptoWalletService.transactionReceived();
		this.done();
		const sub = this.socketService
			.joinRoom({
				name: "wallet_transactions",
				isPrivate: true,
				type: GenericTransaction
			})
			.pipe(
				filter((t) => t.getId() === transaction.getId()),
				filter((t: CryptoTransaction) => t.isStatus("finished") || t.isOpenInvitation() || t.isConfirmed())
			)
			.subscribe((transaction: CryptoTransaction) => {
				if (!transaction.isConfirmed()) {
					return;
				}
				waitingModal.dismiss();
				if (transaction.isOpenInvitation() || transaction.isStatus("finished")) {
					if (sub) {
						sub.unsubscribe();
					}
					this.modalService.create(SendSuccessComponent, { transaction }).present();
				}
			});
		const waitingModal = this.modalService
			.create(SendWaitingComponent, {
				transaction: transaction,
				subForCancel: sub
			})
			.present();
	}

	public finish(): void {
		this.saved = true;
		this.closeAll(true);
	}

	public hasSufficientFunds(): boolean {
		let num = this.amountItemTotal();
		num = stripFunnyInput(num);
		return +num <= this.walletBalance.balance.operationalBalance && +num >= +this.min();
	}

	public toggleSendType(): void {
		this.show = false;
		if (this.checkZeroPrecision()) {
			this.userWants = "cryptocoin";
			this.amount = "" + Math.floor(+this.amount); // When sending a zero precision coin to a bitpanda address, the amount HAS to be floored when toggling to external send!
		}

		const limitsType: string = this.data.transaction._try_internal ? "internal" : "external";
		if (this.hasBothLimitTypesSaved()) {
			const price: Price = this.data.transaction._try_internal
				? this.limitsCollection.getDefaultPriceInternal()
				: this.limitsCollection.getDefaultPriceExternal();
			this.initAmountScreen(price, this.fee, limitsType);
		} else {
			this.fetchLimits(this.fee, limitsType);
		} // Limits are fetched in case external or internal limits-data is missing
		this.amount = "0"; // On send-type switch we reset amount to circumvent weird amount-input issues

		this.ref.detectChanges();
		this.show = true;
	}

	public getCoinPrecision(): number {
		if (this.userWants === "cryptocoin") {
			if (this.checkZeroPrecision()) {
				return 0;
			}
			return this.data.transaction.isInternalSendType() ? 8 : this.item.getPrecisionForTx();
		}
		return 2;
	}

	public change(type: string = "fiat"): void {
		this.show = false;
		if (type === "fiat" || type === "cryptocoin") {
			this.userWants = type;
		} else if (type === "swap") {
			if (this.userWants === "fiat") {
				this.change("cryptocoin");
				this.rudderstackTrack("Trade-By Used", {
					trade_by: this.item.getSymbol(true),
					flow: this.flowName
				});
			} else {
				this.change("fiat");
				this.rudderstackTrack("Trade-By Used", {
					trade_by: this.fiat.getSymbol(true),
					flow: this.flowName
				});
			}
		}
		this.ref.detectChanges();
		this.show = true;
		this.isMax = false;
	}

	// -----------> 1) START OF RECIPIENT VALIDATION
	// TODO: eslint fix

	public getTxPrecision(): boolean {
		if (this.item.getPrecisionForTx() === 0) {
			return !this.data.transaction.isInternalSendType();
		}
		return this.getCoinPrecision() > 0 && !this.data.transaction.isInternalSendType();
	}

	// -----------> 2) FETCHING LIMITS AFTER RECIPIENT VALIDATION OR WHEN SWITCHING SEND-TYPE

	public buyRedirect(): void {
		const offer = new Offer();
		this.modalService.showModal(TradeBuyComponent, offer);
	}

	// -----------> 3) INIT AMOUNT SCREEN AFTER FETCHING LIMITS OR WHEN USING TEMP-SAVE DATA (ALREADY FETCHED INTERNAL AND EXTERNAL)

	public inputOnFocus(e: FocusEvent): void {
		this.trackSelect("amount_input");
	}

	// -----------> METHODS BELOW ONLY NEEDED FOR SEND-TYPE BOTH

	public getTagName(): string {
		if (!this.item) {
			return "destination_tag";
		}
		return this.translationService.translate(this.item.getSymbol(true) + "_destination_tag");
	}

	public showInternalTransferSlider(): boolean {
		return this.isBitpandaAddress() && this.item.isDepositActive() && this.item.isTransferActive();
	}

	// -----------

	// Override
	public modalCanDismiss(): boolean | Observable<boolean> {
		if ((this.dirty && !this.saved) || (this.paymentsFlow && !this.paymentsFlowSuccess && !this.canDismiss)) {
			return this.confirmModalService.showConfirmModal("close_window?", "unsaved_data_will_get_lost").pipe(
				tap((a) => {
					if (a) {
						this.trackCancel();
					}
				})
			);
		}
		return true;
	}

	public resetCoin(): void {
		this.tradableId = null;
		this.doTrapFocusTime();
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			if (this.isFourthStep) {
				this.warningSlider.terms = false;
				this.showExternalWarning = false;
			} else {
				this.isFirstStep ? this.resetCoin() : this.stepBack();
			}
		} else if (name === "abort" && this.isFirstStep) {
			this.close();
		} else if (name === "ok" && this.isEdit(0)) {
			this.selectWallet(this.preselected);
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		switch (this.stepNumber) {
			case 1:
				return this.translate("modal_send_header_summary");
			case 2:
				return this.translate("send_to_CRYPTOSYMBOL_contact", [this.item.getName()]);
			case 3:
				return this.translate("send_to_CRYPTOSYMBOL_contact", [this.item.getName()]);
			case 4:
				return this.translate("send_warning_message_title");
			default:
				return "";
		}
	}

	public onWarningAccept(): void {
		if (!this.warningSlider.terms) {
			this.warningSlider.wiggle("terms");
			return;
		}
		this.showExternalWarning = false;
		this.summary = true;
		this.warningSlider.terms = false; // Slider is not needed beyond this point and can be reset
		this.doTrapFocusTime();
		this.pageChangeSubject.next();
	}

	public getWarningItems(): number[] {
		return [1, 2, 3, 4];
	}

	public getWarningScamsUrl(): string {
		return this.translationService.getHdLink() + config.externalPaths["send"]["onlineScam"];
	}

	public initTracking(): void {
		this.pageChangeSubject
			.pipe(finalize(() => this.rudderstackFlowState(this.saved ? "successful" : "cancelled")))
			.subscribe(() => {
				if (this.summary) {
					this.rudderstackPageFlow("Summary", "summary", {
						asset_category: this.item.getSymbol(true),
						send_amount: this.amount
					});
				} else if (this.isFourthStep) {
					this.rudderstackPageFlow("Warning", "warning", {
						asset_category: this.item.getSymbol(true),
						send_amount: this.amount
					});
				} else if (this.isThirdStep) {
					this.rudderstackPageFlow("Amount", "amount", {
						asset_category: this.item.getSymbol(true)
					});
				} else if (this.isSecondStep) {
					this.rudderstackPageFlow("Destination", "destination", {
						asset_category: this.item.getSymbol(true)
					});
				} else if (this.isFirstStep) {
					this.rudderstackPage("Send Screen", "send");
				}
			});

		this.rudderstackFlowState("started");

		this.pageChangeSubject.next();
	}

	public ngOnDestroy(): void {
		this.pageChangeSubject.complete();
	}

	protected filteredCryptoWallets(): CryptoWallet[] {
		return this.cryptoWalletService
			.getList()
			.getValue()
			.filter((w) => !w.isDeleted())
			.filter(
				(w) =>
					w.getBalance() > 0 &&
					(this.data.transaction.getCurrency().getId() === "" ||
						w.getCurrencyId() === this.data.transaction.getCurrency().getId())
			)
			.filter((w) => this.isWithdrawOrTransfer(w))
			.filter((w) => !this.cryptoCoinService.getItemById(w.getCurrencyId()).isIndexOnly());
	}

	private isWalletSendEnabled(fiatId: string): boolean {
		const fiat = this.getFiatById(fiatId);
		return !!this.masterdataService.payout_providers.find(
			(p) => p.isEnabled() && p.sendEnabled(true) && p.hasFiat(fiat)
		);
	}

	private setPerfectInputMatch(): void {
		this.perfectInputMatch =
			this.getSearchedContactFields().length === 1 &&
			this.getSearchedContactFields()[0].getValue() === this.data.transaction.recipient;
	}

	private checkSendTypeErrorFlags(): boolean {
		// Checks edgecases and sets errors if necessary, returns true if the user needs to be blocked from advancing
		if (this.fee.getAllowSending() === "both") {
			if (!this.item.isTransferActive()) {
				if (!this.item.isDepositActive()) {
					this.data.transaction.setError("recipient", "cannot_send_to_bitpanda_address_right_now");
					return true;
				}
				if (!this.hasEnoughFundsForExternal()) {
					this.data.transaction.setError("recipient", "cannot_send_external_because_wallet_balance");
					return true;
				}
			}
		} else if (this.fee.getAllowSending() === "internal" && !this.item.isTransferActive()) {
			this.data.transaction.setError("recipient", "cannot_send_to_email_right_now");
			return true;
		}
		return false;
	}

	/* eslint-disable @typescript-eslint/no-explicit-any */
	private validateContact(data: any): Observable<Fee> {
		// Sending contact to API for validation, returns possible send-type
		if (this.validationRunning) {
			this.infoService.setWarning(this.translationService.translate("please_wait_ongoing_call_send_check"));
			return NEVER;
		}

		this.validationRunning = true;
		return this.apicallService
			.post("wallet/" + this.wallet.getId() + "/send/validate", new ApicallOption(Fee, data))
			.pipe(
				map((a: Fee[]) => a[0]),
				catchError((error) => {
					Helper.handleError(error, this.data.transaction, {
						forceToast: true,
						callback: () => {
							this.data.transaction.setError("recipient", "validation_error_possible_external");
						}
					});
					this.validationRunning = false;
					return observableOf(null);
				}),
				filter((a) => !!a),
				tap((fee: Fee) => this.fetchLimits(fee)),
				tap(() => (this.validationRunning = false))
			);
	}

	private fetchLimits(fee: Fee, limitsType: string = ""): void {
		// For send-type both -> default send-type is internal
		let sendType: string = fee.getAllowSending() === "both" ? "internal" : fee.getAllowSending();
		if (limitsType) {
			sendType = limitsType;
		}
		this.apicallService
			.post("wallet/" + this.wallet.getId() + "/limits", new ApicallOption(Price, { type: sendType }))
			.pipe(map((p) => p[0]))
			.subscribe(
				(a) => {
					this.setPriceByType(a, sendType);
					this.initAmountScreen(a, fee, limitsType);
				},
				(e) => Helper.handleError(e)
			);
	}

	private initAmountScreen(price: Price, fee: Fee, limitsType: string): void {
		this.price = price;
		this.fee = fee;
		// Send-type is set on data to display amount-tiles correctly
		// If limitsType is handed over, user is switching both-type manually -> no setting on data necessary
		if (!this.isSameRecipient() && !limitsType) {
			this.setDataSendType(fee);
		}
		this.setMaxApiAmount(); // Sets max-amount tile depending on send-type
		this.setWalletAmounts();
	}

	private setPriceByType(price: Price, sendType: string = ""): void {
		if (sendType === "external") {
			this.setDefaultPriceExternal(price);
		} else {
			this.setDefaultPriceInternal(price);
		}
	}

	private hasBothLimitTypesSaved(): boolean {
		return !!this.limitsCollection.getDefaultPriceInternal() && !!this.limitsCollection.getDefaultPriceExternal();
	}

	private setDataSendType(fee: Fee): void {
		if (!this.hasEnoughFundsForExternal()) {
			this.data.transaction.setSendType("internal");
			this.data.transaction.setTryInternal(true);
		} else {
			this.data.transaction.setSendType(fee.getAllowSending());
			this.checkTryInternalTypeEdgecases();
		}
		if (this.checkZeroPrecision()) {
			this.userWants = "cryptocoin";
		}
	}

	private setWalletAmounts(): void {
		const max = this.price.getWallet();
		max.setTradableAmount(this.getMaxApiAmount().tradableAmount());
		max.setFiatAmount(this.getMaxApiAmount().fiatAmount());
	}

	private getMaxApiAmount(): Conversion {
		return this.maxApiAmount;
	}

	private setMaxApiAmount(): void {
		this.maxApiAmount = this.data.transaction.isInternalSendType()
			? this.price.getMaxAmountInternal()
			: this.price.getMaxAmountExternal();
	}

	private clearAmounts(): void {
		this.data.transaction.setAmountCoin("0");
		this.data.transaction.setAmountFiat("0");
	}

	private stellarTag(): void {
		if (!this.isStellar()) {
			return;
		}
		this.data.transaction.setDtType(this.getDtTypes[0].value);
	}

	private getDestinationTag(): string {
		if (this.contactField) {
			return this.contactField.getDestinationTag();
		}
		return this.data.transaction.getDestinationTag();
	}

	private isWithdrawOrTransfer(w: CryptoWallet): boolean {
		const coin: Cryptocoin = this.cryptoCoinService.getItemById(w.getCurrencyId());
		if (!coin) {
			return false;
		}
		return (
			coin.isWithdrawActive() ||
			(coin.isTransferActive() &&
				this.cryptoWalletService
					.getWalletsByTradableId(coin.getId())
					.filter((w: CryptoWallet) => !w.isDeleted()).length > 1)
		);
	}

	private setDefaultPriceExternal(price: Price): void {
		this.limitsCollection.setDefaultPriceExternal(price);
	}

	private setDefaultPriceInternal(price: Price): void {
		this.limitsCollection.setDefaultPriceInternal(price);
	}

	public resetData(): void {
		this.wallet = null;
		this.edit = 0;
	}
}

// Used as a storing model for sending-type "both"
class LimitsCollection {
	private default_price_external: Price = null;
	private default_price_internal: Price = null;

	public getDefaultPriceExternal(): Price {
		return this.default_price_external;
	}

	public setDefaultPriceExternal(price: Price): void {
		this.default_price_external = price;
	}

	public getDefaultPriceInternal(): Price {
		return this.default_price_internal;
	}

	public setDefaultPriceInternal(price: Price): void {
		this.default_price_internal = price;
	}
}

class SendSwitch extends Validateable {
	public terms: boolean = false;
}
